import { useState } from "react";
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { PowerSettingsNew as PowerIcon, Warning as WarningIcon } from "@mui/icons-material";
import {
    useIotDetekceStavQuery,
    useVypnoutIotDetekciMutation,
    useZapnoutIotDetekciMutation,
} from "../../api/JsdhNotifikaceService/service/IotMutations";

const QUICK_HOURS = [1, 4, 8, 24];

const GlobalniVypnutiDetekceSection: React.FC = () => {
    const { data, isLoading } = useIotDetekceStavQuery();
    const vypnoutMutation = useVypnoutIotDetekciMutation();
    const zapnoutMutation = useZapnoutIotDetekciMutation();
    const [duvod, setDuvod] = useState("");

    if (isLoading) {
        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <CircularProgress size={24} />
                </CardContent>
            </Card>
        );
    }

    const aktualneVypnuto = data?.currentlyEnabled === false;
    const vypnutoDo = data?.disabledDo
        ? new Date(data.disabledDo).toLocaleString("cs-CZ")
        : null;

    const vypnoutNaHodin = (hodin: number) => {
        vypnoutMutation.mutate({ hodin, duvod: duvod || undefined });
        setDuvod("");
    };
    const vypnoutTrvale = () => {
        vypnoutMutation.mutate({ trvale: true, duvod: duvod || undefined });
        setDuvod("");
    };
    const zapnout = () => zapnoutMutation.mutate();

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <PowerIcon color={aktualneVypnuto ? "error" : "success"} />
                    <Typography variant="h6">Globální vypnutí detekce</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Vypnutí vyhlašování poplachů z IoT detektorů (Zigbee, Shelly) a EPS/EZS panelů (SIA) —
                    dočasně (max. 24 hodin, poté admin musí znovu potvrdit) nebo trvale do odvolání.
                    KOPIS poplachy nejsou ovlivněny.
                </Typography>

                {aktualneVypnuto ? (
                    <Alert
                        severity="error"
                        icon={<WarningIcon />}
                        action={
                            <Button
                                color="inherit"
                                variant="outlined"
                                onClick={zapnout}
                                disabled={zapnoutMutation.isPending}
                                startIcon={zapnoutMutation.isPending ? <CircularProgress size={14} /> : null}
                            >
                                Zapnout
                            </Button>
                        }
                    >
                        <Typography sx={{ fontWeight: 600 }}>
                            Detekce je VYPNUTA{vypnutoDo ? ` do ${vypnutoDo}` : " trvale"}
                        </Typography>
                        {data?.disabledDuvod && (
                            <Typography variant="body2">Důvod: {data.disabledDuvod}</Typography>
                        )}
                    </Alert>
                ) : (
                    <Box>
                        <TextField
                            label="Důvod (volitelné)"
                            value={duvod}
                            onChange={(e) => setDuvod(e.target.value)}
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 500 }}
                            sx={{ mb: 2 }}
                            placeholder="např. údržba detektorů, test poplachu, malování..."
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                    Vypnout dočasně:
                                </Typography>
                                <ButtonGroup variant="outlined" disabled={vypnoutMutation.isPending}>
                                    {QUICK_HOURS.map((hours) => (
                                        <Button key={hours} onClick={() => vypnoutNaHodin(hours)}>
                                            {hours} h
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                    Nebo:
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={vypnoutTrvale}
                                    disabled={vypnoutMutation.isPending}
                                    startIcon={vypnoutMutation.isPending ? <CircularProgress size={14} color="inherit" /> : null}
                                >
                                    Vypnout do odvolání
                                </Button>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5, maxWidth: 260 }}
                                >
                                    Detekce zůstane vypnutá, dokud ji ručně nezapnete — budeme vám denně připomínat.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default GlobalniVypnutiDetekceSection;
